import { zodResolver } from '@hookform/resolvers/zod';
import { useQueryClient } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';

import { useAmbientesControllerListar } from '@/api/generated/admin-ambientes/admin-ambientes';
import { useCargosControllerListar } from '@/api/generated/admin-cargos/admin-cargos';
import {
  getUsuariosControllerListarQueryKey,
  useUsuariosControllerAtualizar,
  useUsuariosControllerCriar,
  useUsuariosControllerEnviarFoto,
  useUsuariosControllerRemoverFoto,
} from '@/api/generated/admin-usuarios/admin-usuarios';
import type { UsuarioResponse } from '@/api/generated/model';
import { aplicarErrosDoServidor } from '@/api/erros';
import { ApiError } from '@/api/http-client';
import { UFS } from '@/features/auth/schemas/login.schema';
import { Campo, CLASSE_CONTROLE } from '@/shared/components/Campo';
import { CampoDeFoto } from '@/shared/components/CampoDeFoto';
import { usePermissoes } from '@/shared/permissoes/usePermissoes';
import { Alert, AlertDescription } from '@/shared/ui/alert';
import { Button } from '@/shared/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/dialog';
import { Input } from '@/shared/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/ui/select';
import { toast } from '@/shared/ui/sonner';
import { Textarea } from '@/shared/ui/textarea';

/** Sentinela do select de ambiente: "nenhum" = cria um ambiente próprio. */
const AMBIENTE_PROPRIO = 'proprio';
const SEM_UF = 'nenhuma';
/** Cargo cuja bio é obrigatória — o médico vê antes de escolher o advisor. */
const CARGO_EXIGE_BIO = 'assessor';

const schema = z.object({
  nomeCompleto: z.string().trim().min(3, 'Informe o nome completo').max(200),
  crmv: z.string().trim().max(20),
  ufCrmv: z.enum([...UFS, SEM_UF]),
  telefone: z.string().trim().max(20),
  especialidade: z.string().trim().max(120),
  clinica: z.string().trim().max(200),
  bio: z.string().trim().max(1000, 'Máximo de 1000 caracteres.'),

  // Só valem na criação — ver `resolverPara`.
  email: z.string().trim(),
  senha: z.string(),
  cargoChave: z.string(),
  tenantId: z.string(),
});

type Formulario = z.infer<typeof schema>;
type ValorUf = Formulario['ufCrmv'];

/** O `ufCrmv` do servidor é `string | null`; o select só aceita UF conhecida. */
function ufDoServidor(valor: string | null | undefined): ValorUf {
  return UFS.includes(valor as (typeof UFS)[number]) ? (valor as ValorUf) : SEM_UF;
}

/**
 * Um schema só, com os campos de criação validados condicionalmente.
 *
 * Dois schemas dariam dois tipos de formulário, e o `useForm` teria de escolher
 * um — que é como se chega no `as never` que este arranjo evita. Os campos de
 * criação existem sempre no estado; o que muda é se são exigidos.
 *
 * `cargoAtualNaEdicao`: na edição o cargo não é escolhido neste formulário
 * (é a ação separada "Cargo" da lista) — por isso a exigência de bio olha o
 * cargo que o usuário JÁ tem, não um campo do form.
 */
function resolverPara(editando: boolean, cargoAtualNaEdicao: string | null) {
  return zodResolver(
    schema.superRefine((valores, ctx) => {
      if (editando) {
        if (cargoAtualNaEdicao === CARGO_EXIGE_BIO && !valores.bio.trim()) {
          ctx.addIssue({
            code: 'custom',
            path: ['bio'],
            message: 'Bio é obrigatória para o cargo assessor.',
          });
        }
        return;
      }

      if (!z.email().safeParse(valores.email).success) {
        ctx.addIssue({ code: 'custom', path: ['email'], message: 'E-mail inválido' });
      }
      if (valores.senha.length < 8) {
        ctx.addIssue({
          code: 'custom',
          path: ['senha'],
          message: 'A senha precisa de ao menos 8 caracteres',
        });
      }
      if (!valores.cargoChave) {
        ctx.addIssue({ code: 'custom', path: ['cargoChave'], message: 'Selecione um cargo' });
      }
      if (valores.cargoChave === CARGO_EXIGE_BIO && !valores.bio.trim()) {
        ctx.addIssue({
          code: 'custom',
          path: ['bio'],
          message: 'Bio é obrigatória para o cargo assessor.',
        });
      }
    }),
  );
}

interface Props {
  aberto: boolean;
  aoFechar: () => void;
  /** Ausente = criação. */
  usuario?: UsuarioResponse;
}

/**
 * Cria e edita usuário.
 *
 * ⚠️ `papel` não existe neste formulário, nem no da edição. Promover alguém a
 * super admin é operação de script (`npm run superadmin`): uma tela que concede
 * o único bypass de permissão do sistema seria escalada de privilégio a um
 * clique de distância. O servidor recusa o campo com 422.
 */
export function UsuarioFormDialogo({ aberto, aoFechar, usuario }: Props) {
  const queryClient = useQueryClient();
  const { pode } = usePermissoes();
  const editando = Boolean(usuario);

  const criar = useUsuariosControllerCriar();
  const atualizar = useUsuariosControllerAtualizar();
  const enviarFoto = useUsuariosControllerEnviarFoto();
  const removerFoto = useUsuariosControllerRemoverFoto();

  // Upload salva sozinho (independente do Save do form, mesmo padrão de
  // `ImagensDoParceiroDialogo`) — precisa de estado próprio porque `usuario`
  // é só o retrato de quando o diálogo abriu; a resposta do upload é quem
  // sabe a `fotoUrl` nova.
  const [fotoUrl, setFotoUrl] = useState<string | null>(usuario?.fotoUrl ?? null);

  // Só busca o que o formulário de criação usa, e só se o admin puder ler.
  // Sem a permissão, o servidor devolveria 403 e o diálogo mostraria um erro
  // que não tem nada a ver com o que ele está fazendo.
  const podeVerCargos = pode('admin_cargos.visualizar');
  const podeVerAmbientes = pode('admin_tenants.visualizar');

  const { data: cargos } = useCargosControllerListar(
    { pagina: 1, porPagina: 100, ordenarPor: 'nome', ordem: 'asc' },
    { query: { enabled: aberto && !editando && podeVerCargos } },
  );
  const { data: ambientes } = useAmbientesControllerListar(
    { pagina: 1, porPagina: 100, ordenarPor: 'nome', ordem: 'asc', ativo: 'true' },
    { query: { enabled: aberto && !editando && podeVerAmbientes } },
  );

  const {
    register,
    control,
    handleSubmit,
    reset,
    setError,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<Formulario>({ resolver: resolverPara(editando, usuario?.cargo?.chave ?? null) });

  useEffect(() => {
    if (!aberto) return;

    reset({
      nomeCompleto: usuario?.nomeCompleto ?? '',
      crmv: usuario?.crmv ?? '',
      ufCrmv: ufDoServidor(usuario?.ufCrmv),
      telefone: usuario?.telefone ?? '',
      especialidade: usuario?.especialidade ?? '',
      clinica: usuario?.clinica ?? '',
      bio: usuario?.bio ?? '',
      email: '',
      senha: '',
      cargoChave: 'medico',
      tenantId: AMBIENTE_PROPRIO,
    });
    setFotoUrl(usuario?.fotoUrl ?? null);
  }, [aberto, usuario, reset]);

  async function aoEnviarFoto(arquivo: File) {
    if (!usuario) return;
    try {
      const atualizado = await enviarFoto.mutateAsync({ id: usuario.id, data: { arquivo } });
      setFotoUrl(atualizado.fotoUrl);
      await queryClient.invalidateQueries({ queryKey: getUsuariosControllerListarQueryKey() });
      toast.success('Foto atualizada.');
    } catch (erro) {
      toast.error(erro instanceof ApiError ? erro.message : 'Não foi possível enviar a foto.');
    }
  }

  async function aoRemoverFoto() {
    if (!usuario) return;
    try {
      const atualizado = await removerFoto.mutateAsync({ id: usuario.id });
      setFotoUrl(atualizado.fotoUrl);
      await queryClient.invalidateQueries({ queryKey: getUsuariosControllerListarQueryKey() });
      toast.success('Foto removida.');
    } catch (erro) {
      toast.error(erro instanceof ApiError ? erro.message : 'Não foi possível remover a foto.');
    }
  }

  const cargoChaveDigitado = watch('cargoChave');
  const bioDigitada = watch('bio') ?? '';
  // Na criação, mostra o campo assim que "assessor" é escolhido. Na edição, o
  // cargo já é fixo (esta tela não muda cargo) — mostra se já é assessor.
  const mostrarBio = editando
    ? usuario?.cargo?.chave === CARGO_EXIGE_BIO
    : cargoChaveDigitado === CARGO_EXIGE_BIO;

  const aoEnviar = handleSubmit(async (dados) => {
    // Campo vazio na edição significa "apagar", não "não mexer": o backend
    // aceita null nesses campos e o formulário mostra o valor atual, então o
    // que o admin vê é o que ele envia.
    const opcional = (v: string) => (v.trim() ? v.trim() : null);
    const uf = dados.ufCrmv === SEM_UF ? null : dados.ufCrmv;

    try {
      if (usuario) {
        await atualizar.mutateAsync({
          id: usuario.id,
          data: {
            nomeCompleto: dados.nomeCompleto,
            crmv: opcional(dados.crmv),
            ufCrmv: uf,
            telefone: opcional(dados.telefone),
            especialidade: opcional(dados.especialidade),
            clinica: opcional(dados.clinica),
            ...(mostrarBio ? { bio: opcional(dados.bio) } : {}),
          },
        });
      } else {
        const crmv = opcional(dados.crmv);
        const telefone = opcional(dados.telefone);
        const especialidade = opcional(dados.especialidade);
        const clinica = opcional(dados.clinica);
        const bio = opcional(dados.bio);

        // Na criação o schema do servidor é `.strict()` e os opcionais não
        // aceitam null — por isso o campo em branco é OMITIDO, não enviado
        // vazio.
        await criar.mutateAsync({
          data: {
            email: dados.email,
            senha: dados.senha,
            nomeCompleto: dados.nomeCompleto,
            cargoChave: dados.cargoChave,
            ...(dados.tenantId !== AMBIENTE_PROPRIO ? { tenantId: dados.tenantId } : {}),
            ...(crmv ? { crmv } : {}),
            ...(uf ? { ufCrmv: uf } : {}),
            ...(telefone ? { telefone } : {}),
            ...(especialidade ? { especialidade } : {}),
            ...(clinica ? { clinica } : {}),
            ...(bio ? { bio } : {}),
          },
        });
      }

      await queryClient.invalidateQueries({ queryKey: getUsuariosControllerListarQueryKey() });
      toast.success(editando ? 'Usuário atualizado.' : 'Usuário criado.');
      aoFechar();
    } catch (erro) {
      aplicarErrosDoServidor(erro, setError, 'Não foi possível salvar o usuário.');
    }
  });

  return (
    <Dialog open={aberto} onOpenChange={(v) => !v && aoFechar()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{editando ? 'Editar usuário' : 'Novo usuário'}</DialogTitle>
          <DialogDescription>
            {editando
              ? 'E-mail, cargo e status são alterados pelas outras ações da lista.'
              : 'A senha é entregue ao usuário por fora — não há e-mail de convite ainda.'}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={aoEnviar} className="space-y-4" noValidate>
          {errors.root?.message && (
            <Alert variant="destructive">
              <AlertDescription>{errors.root.message}</AlertDescription>
            </Alert>
          )}

          {editando && (
            <div className="flex justify-center pb-2">
              <CampoDeFoto
                nome={usuario?.nomeCompleto}
                urlAtual={fotoUrl}
                enviando={enviarFoto.isPending || removerFoto.isPending}
                aoEnviar={(arquivo) => void aoEnviarFoto(arquivo)}
                aoRemover={() => void aoRemoverFoto()}
              />
            </div>
          )}

          {!editando && (
            <>
              <Campo id="email" rotulo="E-mail" erro={errors.email?.message} obrigatorio>
                <Input
                  id="email"
                  type="email"
                  autoComplete="off"
                  className={CLASSE_CONTROLE}
                  {...register('email')}
                />
              </Campo>

              <Campo
                id="senha"
                rotulo="Senha inicial"
                erro={errors.senha?.message}
                obrigatorio
                dica="Mínimo de 8 caracteres. O usuário pode trocá-la depois pelo próprio perfil."
              >
                <Input
                  id="senha"
                  type="text"
                  autoComplete="off"
                  className={CLASSE_CONTROLE}
                  {...register('senha')}
                />
              </Campo>
            </>
          )}

          <Campo
            id="nomeCompleto"
            rotulo="Nome completo"
            erro={errors.nomeCompleto?.message}
            obrigatorio
          >
            <Input id="nomeCompleto" className={CLASSE_CONTROLE} {...register('nomeCompleto')} />
          </Campo>

          <div className="grid grid-cols-3 gap-3">
            <Campo id="crmv" rotulo="CRMV" erro={errors.crmv?.message} className="col-span-2">
              <Input id="crmv" className={CLASSE_CONTROLE} {...register('crmv')} />
            </Campo>

            <Campo id="ufCrmv" rotulo="UF" erro={errors.ufCrmv?.message}>
              <Controller
                control={control}
                name="ufCrmv"
                render={({ field }) => (
                  <Select value={field.value} onValueChange={field.onChange}>
                    <SelectTrigger id="ufCrmv" className={CLASSE_CONTROLE}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={SEM_UF}>—</SelectItem>
                      {UFS.map((uf) => (
                        <SelectItem key={uf} value={uf}>
                          {uf}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              />
            </Campo>
          </div>

          <Campo id="telefone" rotulo="Telefone" erro={errors.telefone?.message}>
            <Input id="telefone" className={CLASSE_CONTROLE} {...register('telefone')} />
          </Campo>

          <div className="grid grid-cols-2 gap-3">
            <Campo id="especialidade" rotulo="Especialidade" erro={errors.especialidade?.message}>
              <Input
                id="especialidade"
                className={CLASSE_CONTROLE}
                {...register('especialidade')}
              />
            </Campo>

            <Campo id="clinica" rotulo="Clínica" erro={errors.clinica?.message}>
              <Input id="clinica" className={CLASSE_CONTROLE} {...register('clinica')} />
            </Campo>
          </div>

          {editando && mostrarBio && (
            <>
              <Campo
                id="bio"
                rotulo="Bio"
                erro={errors.bio?.message}
                obrigatorio
                dica="Texto de apresentação como especialista — o médico vê antes de escolher."
              >
                <Textarea id="bio" rows={4} className={CLASSE_CONTROLE} {...register('bio')} />
              </Campo>
              <p className="text-right text-xs text-muted-foreground -mt-3">
                {bioDigitada.length}/1000
              </p>
            </>
          )}

          {!editando && (
            <>
              <Campo
                id="cargoChave"
                rotulo="Cargo"
                erro={errors.cargoChave?.message}
                obrigatorio
                dica={
                  podeVerCargos ? undefined : 'Sem permissão para listar cargos: será "medico".'
                }
              >
                <Controller
                  control={control}
                  name="cargoChave"
                  render={({ field }) => (
                    <Select
                      value={field.value}
                      onValueChange={field.onChange}
                      disabled={!podeVerCargos}
                    >
                      <SelectTrigger id="cargoChave" className={CLASSE_CONTROLE}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {(cargos?.itens ?? []).map((cargo) => (
                          <SelectItem key={cargo.id} value={cargo.chave}>
                            {cargo.nome}
                          </SelectItem>
                        ))}
                        {!cargos && <SelectItem value="medico">Médico veterinário</SelectItem>}
                      </SelectContent>
                    </Select>
                  )}
                />
              </Campo>

              {mostrarBio && (
                <>
                  <Campo
                    id="bio"
                    rotulo="Bio"
                    erro={errors.bio?.message}
                    obrigatorio
                    dica="Texto de apresentação como especialista — o médico vê antes de escolher."
                  >
                    <Textarea id="bio" rows={4} className={CLASSE_CONTROLE} {...register('bio')} />
                  </Campo>
                  <p className="text-right text-xs text-muted-foreground -mt-3">
                    {bioDigitada.length}/1000
                  </p>
                </>
              )}

              <Campo
                id="tenantId"
                rotulo="Ambiente"
                erro={errors.tenantId?.message}
                dica="Sem escolher um, o usuário ganha um ambiente individual só dele."
              >
                <Controller
                  control={control}
                  name="tenantId"
                  render={({ field }) => (
                    <Select
                      value={field.value}
                      onValueChange={field.onChange}
                      disabled={!podeVerAmbientes}
                    >
                      <SelectTrigger id="tenantId" className={CLASSE_CONTROLE}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value={AMBIENTE_PROPRIO}>Criar ambiente próprio</SelectItem>
                        {(ambientes?.itens ?? []).map((ambiente) => (
                          <SelectItem key={ambiente.id} value={ambiente.id}>
                            {ambiente.nome}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                />
              </Campo>
            </>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={aoFechar} disabled={isSubmitting}>
              Cancelar
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {editando ? 'Salvar' : 'Criar usuário'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
